import { createHarbourPuzzle, HARBOUR_GAME_EQUIPMENT, HARBOUR_GAME_KINDS, HARBOUR_GAME_LABELS,
  harbourGameClearKey, netStateAfter, SIGNAL_SYMBOLS, validateHarbourGameProof,
  type HarbourGameDifficulty, type HarbourGameKind, type HarbourGameProof } from './harbour-games';
import './harbour-games.css';

export interface HarbourGameCompletion { readonly ok: boolean; readonly message?: string; }
export interface HarbourGamePanelOptions {
  readonly harbourId: string;
  readonly advancedKinds?: readonly HarbourGameKind[];
  readonly clearedKeys?: readonly string[];
  readonly onComplete: (proof: HarbourGameProof) => HarbourGameCompletion | Promise<HarbourGameCompletion>;
  readonly onBack?: () => void;
}
export interface HarbourGamePanel { destroy(): void; }
const DESCRIPTIONS: Readonly<Record<HarbourGameKind, string>> = {
  'line-mending': 'Repair a net by changing neighboring patches.',
  'harbour-signals': 'Study a signal order, then send it from memory.',
  'tidal-pilot': 'Plot a safe course across a small paper chart.',
};

/** Mounted once by the harbour sheet. There is no timer or frame subscription;
 * intentional game/difficulty changes rebuild a board, while moves only update
 * existing controls so keyboard/gamepad focus survives every move.
 */
export function mountHarbourGamePanel(host: HTMLElement, options: HarbourGamePanelOptions): HarbourGamePanel {
  const document = host.ownerDocument, cleared = new Set(options.clearedKeys ?? []);
  let alive = true, revision = 0;
  const root = document.createElement('section'); root.className = 'harbour-games';
  root.setAttribute('aria-label', 'Optional harbour games'); host.append(root);
  function element<K extends keyof HTMLElementTagNameMap>(tag: K, text = '', className = ''): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag); node.textContent = text;
    if (className) node.className = className;
    return node;
  }
  function button(text: string, action: () => void, className = ''): HTMLButtonElement {
    const node = element('button', text, className); node.type = 'button'; node.addEventListener('click', action); return node;
  }
  function menu(): void {
    revision++; host.scrollTop = 0;
    root.replaceChildren(element('h3', 'Optional harbour games'),
      element('p', 'Try a puzzle with the harbour crew. First clears earn 50 coins on Basic or 150 on Advanced. Fishing carries on; there is no time limit.', 'harbour-games-intro'));
    const choices = element('div', '', 'harbour-game-choices');
    for (const kind of HARBOUR_GAME_KINDS) {
      const row = element('div', '', 'harbour-game-choice');
      row.append(button(HARBOUR_GAME_LABELS[kind], () => game(kind, 'basic')), element('p', DESCRIPTIONS[kind]));
      choices.append(row);
    }
    root.append(choices);
    if (options.onBack) root.append(button('Back to harbour', options.onBack));
  }
  function game(kind: HarbourGameKind, difficulty: HarbourGameDifficulty): void {
    const currentRevision = ++revision, puzzle = createHarbourPuzzle(options.harbourId, kind, difficulty);
    const key = harbourGameClearKey(options.harbourId, kind, difficulty);
    let moves: number[] = puzzle.kind === 'tidal-pilot' ? [puzzle.start] : [];
    let solved = false, busy = false, studied = false, recalling = false;
    let retryProof: HarbourGameProof | null = null;
    const heading = element('h3', HARBOUR_GAME_LABELS[kind]); heading.tabIndex = -1;
    const choose = button('Choose another game', () => { menu(); root.querySelector('button')?.focus(); });
    const levels = element('div', '', 'harbour-game-levels');
    const basic = button('Basic', () => game(kind, 'basic')); basic.setAttribute('aria-pressed', String(difficulty === 'basic')); levels.append(basic);
    if (options.advancedKinds?.includes(kind)) {
      const advanced = button('Advanced', () => game(kind, 'advanced')); advanced.setAttribute('aria-pressed', String(difficulty === 'advanced')); levels.append(advanced);
    } else levels.append(element('p', `Advanced requires the ${HARBOUR_GAME_EQUIPMENT[kind].label}.`, 'harbour-game-locked'));
    const mode = element('p', cleared.has(key) ? 'Practice · already cleared here. No extra coins.' : `First clear: ${difficulty === 'advanced' ? 150 : 50} coins at this harbour.`, 'harbour-game-mode');
    const instructions = puzzle.kind === 'line-mending'
      ? 'Select a patch to change it and its neighbors above, below, left and right. Mend every damaged patch. A sound patch may become torn again.'
      : puzzle.kind === 'harbour-signals'
        ? 'Show the sequence and study it for as long as you like. Start recall hides it; choose the same symbols in the same order. No time limit.'
        : 'This is a chart puzzle; it does not steer your fishing boat. Begin at Start and choose neighboring water squares to reach Quay. Avoid shoals; move up, down, left or right.';
    const text = element('div', '', 'harbour-game-explanation');
    text.append(element('p', instructions), mode);
    const workspace = element('div', '', 'harbour-game-workspace');
    const board = element('div', '', 'harbour-game-board');
    board.setAttribute('role', 'group'); board.setAttribute('aria-label', HARBOUR_GAME_LABELS[kind]);
    const cells: HTMLButtonElement[] = [];
    const signalButtons: HTMLButtonElement[] = [];
    const sequence = element('ol', '', 'harbour-signal-sequence'); sequence.dataset.sequence = ''; sequence.hidden = true;
    const recalled = element('p', '', 'harbour-signal-recalled');
    const status = element('p', '', 'harbour-game-status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); status.tabIndex = -1;
    const actions = element('div', '', 'harbour-game-actions');
    const reset = button('Reset puzzle', resetPuzzle);
    const finish = button(puzzle.kind === 'line-mending' ? 'Check net' : puzzle.kind === 'harbour-signals' ? 'Send signals' : 'Check course', check);
    const retry = button('Try recording again', () => { if (retryProof) void record(retryProof); }); retry.hidden = true;
    const undo = button('Undo last move', () => {
      if (busy || solved) return;
      if (moves.length > (puzzle.kind === 'tidal-pilot' ? 1 : 0)) moves.pop();
      refresh(); status.textContent = 'Last move undone.';
    });
    const show = button('Show sequence', () => {
      if (busy || solved) return;
      studied = true; recalling = false; moves = []; sequence.hidden = false;
      refresh(); status.textContent = 'Study the symbols. Choose Start recall when you are ready.';
    });
    const start = button('Start recall', () => {
      if (!studied || busy || solved) return;
      recalling = true; moves = []; sequence.hidden = true;
      refresh(); signalButtons[0]?.focus(); status.textContent = 'Send the symbols in the order you studied.';
    });

    function refresh(): void {
      reset.disabled = busy; finish.disabled = busy || solved; undo.disabled = busy || solved || moves.length <= (puzzle.kind === 'tidal-pilot' ? 1 : 0);
      choose.disabled = busy; basic.disabled = busy;
      for (const control of levels.querySelectorAll('button')) control.disabled = busy;
      if (puzzle.kind === 'line-mending') {
        const damaged = netStateAfter(puzzle, moves);
        cells.forEach((cell, i) => {
          cell.dataset.state = damaged[i] ? 'damaged' : 'sound'; cell.textContent = damaged[i] ? '×' : '+';
          cell.setAttribute('aria-label', `Row ${Math.floor(i / puzzle.size) + 1}, column ${i % puzzle.size + 1}: ${damaged[i] ? 'damaged' : 'sound'} patch`);
          cell.setAttribute('aria-pressed', String(!damaged[i])); cell.disabled = busy || solved;
        });
      } else if (puzzle.kind === 'harbour-signals') {
        show.disabled = busy || solved; start.disabled = busy || solved || !studied;
        signalButtons.forEach(control => { control.disabled = busy || solved || !recalling || moves.length >= puzzle.sequence.length; });
        recalled.textContent = `Your signals (${moves.length}/${puzzle.sequence.length}): ${moves.map(i => SIGNAL_SYMBOLS[i]!.name).join(' · ') || 'none yet'}`;
      } else {
        cells.forEach((cell, i) => {
          const shoal = puzzle.shoals.includes(i), step = moves.indexOf(i), current = moves[moves.length - 1] === i;
          cell.dataset.state = shoal ? 'shoal' : current ? 'current' : step >= 0 ? 'course' : 'water';
          cell.textContent = i === puzzle.start ? 'Start' : i === puzzle.quay ? 'Quay' : shoal ? 'Shoal' : step >= 0 ? String(step) : '·';
          cell.setAttribute('aria-label', `Row ${Math.floor(i / puzzle.size) + 1}, column ${i % puzzle.size + 1}: ${i === puzzle.start ? 'Start' : i === puzzle.quay ? 'Quay' : shoal ? 'shoal' : 'water'}${current ? ', current position' : step >= 0 ? ', on your course' : ''}`);
          cell.setAttribute('aria-pressed', String(step >= 0)); cell.disabled = busy || solved || shoal;
        });
      }
    }
    function resetPuzzle(): void {
      if (busy) return;
      moves = puzzle.kind === 'tidal-pilot' ? [puzzle.start] : [];
      solved = false; studied = false; recalling = false; retryProof = null; retry.hidden = true; sequence.hidden = true;
      mode.textContent = cleared.has(key) ? 'Practice · already cleared here. No extra coins.' : `First clear: ${difficulty === 'advanced' ? 150 : 50} coins at this harbour.`;
      refresh(); status.textContent = 'Puzzle reset. Take your time.';
      host.scrollTop = 0; heading.focus({ preventScroll: true });
    }
    async function record(proof: HarbourGameProof): Promise<void> {
      if (busy) return;
      busy = true; retry.hidden = true; refresh(); status.textContent = 'Recording your result…';
      let result: HarbourGameCompletion;
      try { result = await options.onComplete(proof); }
      catch { result = { ok: false, message: 'Could not record the result. Try again.' }; }
      if (!alive || revision !== currentRevision) return;
      busy = false;
      if (result.ok) {
        solved = true; cleared.add(key); retryProof = null;
        mode.textContent = 'Cleared here · replay for practice. No extra coins.';
        status.textContent = result.message ?? 'Solved. Your harbour result is recorded.';
      } else {
        retryProof = proof; retry.hidden = false;
        status.textContent = result.message ?? 'Could not record the result. Try again.';
      }
      refresh(); status.focus();
    }
    function check(): void {
      if (busy || solved) return;
      const proof: HarbourGameProof = { version: 1, harbourId: options.harbourId, kind, difficulty, moves: [...moves] };
      const result = validateHarbourGameProof(proof);
      if (!result.ok) { status.textContent = result.reason; return; }
      void record(result.proof);
    }
    if (puzzle.kind === 'harbour-signals') {
      sequence.setAttribute('aria-label', 'Sequence to remember');
      for (const i of puzzle.sequence) sequence.append(element('li', `${SIGNAL_SYMBOLS[i]!.glyph} ${SIGNAL_SYMBOLS[i]!.name}`));
      const study = element('div', '', 'harbour-game-actions'); study.append(show, start);
      const symbols = element('div', '', 'harbour-signal-buttons');
      SIGNAL_SYMBOLS.forEach((symbol, index) => {
        const control = button(`${symbol.glyph} ${symbol.name}`, () => {
          if (!recalling || busy || solved || moves.length >= puzzle.sequence.length) return;
          moves.push(index); refresh(); status.textContent = `${moves.length} of ${puzzle.sequence.length} signals chosen.`;
        });
        control.dataset.signal = String(index); signalButtons.push(control); symbols.append(control);
      });
      board.append(study, sequence, symbols, recalled);
    } else {
      board.classList.add('harbour-game-grid'); board.style.setProperty('--harbour-grid-size', String(puzzle.size));
      for (let i = 0; i < puzzle.size * puzzle.size; i++) {
        const cell = button('', () => {
          if (busy || solved) return;
          if (puzzle.kind === 'line-mending') {
            moves.push(i); if (moves.length > 256) { moves.pop(); status.textContent = 'Reset the puzzle to try a fresh route through the net.'; return; }
            refresh(); status.textContent = `${netStateAfter(puzzle, moves).filter(Boolean).length} damaged patches remain.`;
          } else {
            const current = moves[moves.length - 1]!;
            const gap = Math.abs(current % puzzle.size - i % puzzle.size) + Math.abs(Math.floor(current / puzzle.size) - Math.floor(i / puzzle.size));
            if (puzzle.shoals.includes(i) || moves.includes(i) || gap !== 1) { status.textContent = 'Choose a neighboring water square. Use Undo to change your course.'; return; }
            moves.push(i); refresh(); status.textContent = i === puzzle.quay ? 'The course reaches Quay. Check your course to finish.' : `${moves.length - 1} course steps plotted.`;
          }
        }, 'harbour-game-cell');
        cell.dataset.cell = String(i);
        cell.addEventListener('keydown', event => {
          const step = event.key === 'ArrowUp' ? -puzzle.size : event.key === 'ArrowDown' ? puzzle.size : event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
          if (!step) return;
          event.preventDefault(); event.stopPropagation();
          const next = i + step;
          if (next >= 0 && next < cells.length && (Math.abs(step) !== 1 || Math.floor(next / puzzle.size) === Math.floor(i / puzzle.size)) && !cells[next]!.disabled) cells[next]!.focus();
        });
        cells.push(cell); board.append(cell);
      }
    }
    actions.append(finish, undo, reset, retry);
    const gameHeader = element('div', '', 'harbour-game-header');gameHeader.append(heading,choose);
    text.prepend(levels);text.append(actions,status);
    workspace.append(text, board);
    root.replaceChildren(gameHeader,workspace);
    refresh(); host.scrollTop = 0; heading.focus({ preventScroll: true });
  }
  menu();
  return { destroy: () => { alive = false; revision++; root.remove(); } };
}
